import React, { useEffect, useMemo, useState } from 'react';
import { generatePrivateKey, privateKeyToAccount } from 'viem/accounts';
import { ShoppingBag } from 'lucide-react';
import { makeIntent, compare, short } from '@/lib/intentDemo';
import { IntentComparison } from '@/components/spec/demo/IntentComparison';

export function DemoWrongItem() {
  const person = useMemo(() => privateKeyToAccount(generatePrivateKey()), []);
  const [confirmed, setConfirmed] = useState(false);
  const [checked, setChecked] = useState(false);
  const [sig, setSig] = useState('');
  const rec = useMemo(() => makeIntent(confirmed), [confirmed]);
  const result = useMemo(() => compare(rec), [rec]);
  useEffect(() => { person.signMessage({ message: { raw: rec.digest } }).then(setSig); }, [person, rec.digest]);
  return (
    <section className="rounded-2xl border border-fuchsia-500/25 bg-fuchsia-500/[0.03] p-6 mb-6" data-testid="demo-wrong-item">
      <h2 className="text-base md:text-lg font-semibold mb-1 flex items-center gap-2"><ShoppingBag className="w-4 h-4 text-fuchsia-300" /> Wrong-item case. What did the person actually ask for?</h2>
      <p className="text-sm text-gray-400 mb-4 max-w-3xl">The person signed a Purchase Intent before the agent shopped. When the box arrives, the order is compared with it field by field, so "I never asked for that" can be checked instead of argued.</p>
      <div className="grid lg:grid-cols-2 gap-4 [&>*]:min-w-0">
        <div className="rounded-xl border border-white/10 bg-black/30 p-4" data-testid="intent-record">
          <div className="text-xs uppercase tracking-widest text-gray-500 mb-2">Signed Purchase Intent, version {rec.intent.version}</div>
          <ul className="space-y-1.5 text-sm mb-3">
            <li className="text-gray-300">shop.example, up to $120.00 USD, single purchase</li>
            {rec.terms.map((t) => (
              <li key={t.name} className="text-gray-300" data-testid={`intent-term-${t.name.toLowerCase()}`}><span className="text-white font-semibold">{t.name}:</span> {t.value} <span className="text-xs text-gray-500">({t.origin})</span></li>
            ))}
          </ul>
          <div className="font-mono text-[11px] text-gray-500 break-all space-y-0.5">
            <div>termsHash {short(rec.intent.termsHash)}</div>
            <div data-testid="intent-digest">digest {short(rec.digest)}</div>
            <div>signed by person {short(person.address)}{sig && <>, sig {short(sig)}</>}</div>
          </div>
          <label className="flex items-start gap-2 text-sm text-white cursor-pointer mt-4">
            <input type="checkbox" className="accent-amber-300 mt-1" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} data-testid="intent-confirmed-toggle" />
            <span>Person confirmed the change<span className="block text-xs text-gray-500">Size 10 was sold out. The agent proposed size 9 and the person approved that one term on its own, as a new signed version.</span></span>
          </label>
          <button type="button" onClick={() => setChecked(true)} className="mt-4 rounded-full bg-fuchsia-300 text-black px-4 py-2 text-sm font-semibold hover:bg-fuchsia-200 transition-colors" data-testid="intent-compare-btn">The box arrived: compare the order</button>
        </div>
        <IntentComparison checked={checked} result={result} />
      </div>
    </section>
  );
}
